import React, { useContext, useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { tryClaimAutoOpen } from "../tickets/ticketPurchaseDialogCoordinator"
import { useNavigateToPost } from "../../hooks/useNavigateToPost"
import { EventDateFormat, EventTimeFormat } from "../../util/dates"
import { getTicketPriceDisplay } from "@util/ticketUtils"
import FreeTextFormat from "../shared/FreeTextFormat"
import CommentSectionPreview from "../comments/CommentSectionPreview"
import { isPast } from "date-fns"
import { UserContext } from "../HomeApp"
import { TextAsImage } from "./TextAsImage"
import InterestedBlock from "./InterestedBlock"
import ConcertLineup from "../shared/ConcertLineup"
import { ShareUrlButton } from "../ShareUrlButton"
import { ShowHideButton } from "../shared/Button"
import { ReactionBlock } from "./ReactionBlock"
import RelatedPosts from "../RelatedPosts"
import PremiereHeader from "./PremiereHeader"
import TicketPurchaseComponent from "../tickets/TicketPurchaseComponent"
import TicketPurchaseComponentLoggedOut from "../tickets/TicketPurchaseComponentLoggedOut"
import clsx from "clsx"
import type { ConcertDisplayConfig } from "../../types/concertDisplay"
import { DEFAULT_CONCERT_DISPLAY_CONFIG } from "../../types/concertDisplay"



const styles = {
  header: "text-base md:text-2xl font-bold text-black-800 leading-none md:leading-tight",
  bandName: "text-gray-500 leading-none text-xs",
  postDate: "text-xs md:text-sm text-black-800 leading-none md:leading-normal",
  venue: "text-xs md:text-sm",
  description: "text-xs md:text-sm text-gray-500 mt-1",
}

interface ConcertListItemShortProps {
  concert: any
  adminMode?: boolean
  updateStatus?: () => void
  config?: ConcertDisplayConfig
}

export const ConcertListItemShort = ({
  concert,
  adminMode = false,
  updateStatus = () => {},
  config = DEFAULT_CONCERT_DISPLAY_CONFIG,
}: ConcertListItemShortProps) => {
  const [postStatus, setPostStatus] = useState(concert.status)
  const { user } = useContext(UserContext)
  const location = useLocation()
  const acceptedCollaboratorNames = (concert.post_collaborators ?? [])
    .filter((c: any) => c.status === "accepted")
    .map((c: any) => c.band_name)
  const isPastEvent = concert.end_dt ? isPast(new Date(concert.end_dt)) : false
  const navigateToPostScreen = useNavigateToPost(concert.nada_path)

  const urlSaysAutoOpen =
    new URLSearchParams(location.search).get("auto-open-ticket-purchase") ===
    "true"

  const [shouldAutoOpenTicketPurchase, setShouldAutoOpenTicketPurchase] =
    useState(false)

  useEffect(() => {
    if (urlSaysAutoOpen) {
      const claimed = tryClaimAutoOpen()
      if (claimed) {
        setShouldAutoOpenTicketPurchase(true)
      } else {
        setShouldAutoOpenTicketPurchase(false)
      }
    }
  }, [urlSaysAutoOpen, concert.id])

  const isCanceled = concert.status === "canceled" || concert.canceled_at
  const isUnlisted = concert.status === "unpublished"

  return (
    <div>
      <div className="flex gap-3 items-start">
        <div className="flex-1 min-w-0">
          <PremiereHeader post={concert} size="small" />

          <div className={isCanceled ? "text-bn-neon-red" : ""}>
            <div className="flex gap-1">
              <h2
                className={clsx(styles.header, "cursor-pointer")}
                onClick={navigateToPostScreen}
              >
                {isCanceled ? (
                  <>
                    Canceled!{" "}
                    <span
                      className="line-through"
                      dangerouslySetInnerHTML={{ __html: concert.title }}
                    />
                  </>
                ) : (
                  <>
                    <span
                      dangerouslySetInnerHTML={{ __html: concert.title }}
                    />
                    {isUnlisted && (
                      <span className="text-xs font-normal text-gray-500">
                        {" "}
                        (unlisted)
                      </span>
                    )}
                  </>
                )}{" "}
                <ShareUrlButton nadaUrl={concert.nada_url} renderMode="icon" />
              </h2>
            </div>
            {isCanceled && concert.canceled_event_message ? (
              <p className="text-xs mt-1 text-gray-700">
                {concert.canceled_event_message}
              </p>
            ) : null}
          </div>

          {(() => {
            const price = getTicketPriceDisplay(concert)
            if (!price && !concert.ticket_link_url) return null
            return (
              <div className="font-bold text-sm">
                {price}
                {price && concert.ticket_link_url && " - "}
                {concert.ticket_link_url && (
                  <a
                    rel="noreferrer"
                    href={concert.ticket_link_url}
                    target="_blank"
                    className="text-blue-600"
                  >
                    Ticket Link
                  </a>
                )}
              </div>
            )
          })()}
          <div className={clsx(styles.postDate, isCanceled && "line-through")}>
            {EventDateFormat(concert.start_dt, concert.timezone)}~
            {EventTimeFormat(concert.end_dt, concert.timezone)}
            &nbsp;
            <a
              href={concert.add_to_calendar_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700"
            >
              (+)
            </a>
          </div>

          {concert.event_type === "bNliveStream" ? (
            <div className={styles.venue}>
              <a href={concert.nada_url} className="text-gray-700">
                {concert.nada_url}
              </a>
            </div>
          ) : concert.bn_venue ? (
            <div className={styles.venue}>
              <Link
                to={concert.bn_venue.bandnada_path}
                className="text-green-700 font-bold leading-none"
              >
                {concert.bn_venue.name}
              </Link>
            </div>
          ) : concert.venue_name || concert.venue_city_display_name ? (
            <div className={styles.venue}>
              <a
                className="text-gray-700"
                href={concert.google_map_url}
                target="_blank"
                rel="noreferrer"
              >
                {concert.venue_name && concert.venue_city_display_name
                  ? `${concert.venue_name} in ${concert.venue_city_display_name}`
                  : `${concert.venue_name}${concert.venue_city_display_name}`}
              </a>
            </div>
          ) : null}

          <ConcertLineup
            postId={concert.id}
            startTime={concert.start_dt}
            timezone={concert.timezone}
            mode={config.showLineup}
            className="my-0"
          />

          <Link
            className={clsx(styles.bandName, "italic")}
            to={concert.local_path}
          >
            posted by {concert.band_name}
            {acceptedCollaboratorNames.length > 0 &&
              ` w/ ${acceptedCollaboratorNames.join(", ")}`}
          </Link>

          <div className="mt-2">
            {isCanceled ? (
              concert.ticket_type === "bNTickets" &&
              concert.user_ticket_purchase_status ===
                "cancelled_and_refunded" ? (
                <p className="text-xs text-gray-600">You have been refunded.</p>
              ) : null
            ) : concert.ticket_type === "bNTickets" && !isPastEvent ? (
              user ? (
                <TicketPurchaseComponent
                  bandId={concert.band_id}
                  bandName={concert.band_name}
                  userName={user.name}
                  userDefaultPaymentMethodLastFour={
                    user.default_payment_method_last_four
                  }
                  userDefaultPaymentMethodExpired={
                    user.default_payment_method_expired
                  }
                  postId={concert.id}
                  ticketPrice={concert.ticket_price}
                  maxTickets={concert.band_nada_ticket_max}
                  ticketsSold={concert.band_nada_tickets_sold}
                  eventEndDate={concert.end_dt}
                  autoOpenDialog={shouldAutoOpenTicketPurchase}
                  ticketPriceAtDoor={concert.ticket_price_at_door}
                  concert={concert}
                />
              ) : (
                <TicketPurchaseComponentLoggedOut
                  postId={concert.id}
                  bandId={concert.band_id}
                  bandName={concert.band_name}
                  ticketPrice={concert.ticket_price}
                  maxTickets={concert.band_nada_ticket_max}
                  ticketsSold={concert.band_nada_tickets_sold}
                  eventEndDate={concert.end_dt}
                  ticketPriceAtDoor={concert.ticket_price_at_door}
                  concert={concert}
                />
              )
            ) : !isPastEvent ? (
              <InterestedBlock
                postId={concert.id}
                renderMode="minimal"
                maxRsvps={concert.max_rsvps}
              />
            ) : null}
          </div>
        </div>

        <div className="hidden sm:block w-1/4 flex-shrink-0 bg-black">
          {concert.cover_image_url_medium ? (
            <img
              src={concert.cover_image_url_medium}
              alt={concert.title}
              onClick={navigateToPostScreen}
              className="aspect-square object-cover w-full cursor-pointer"
            />
          ) : concert.event_type !== "bNliveStream" &&
            concert.band_cover_photo_url_medium ? (
            <img
              src={concert.band_cover_photo_url_medium}
              alt={concert.band_name}
              onClick={navigateToPostScreen}
              className="aspect-square object-cover w-full cursor-pointer"
            />
          ) : (
            <button
              className="cursor-pointer w-full aspect-square"
              onClick={navigateToPostScreen}
            >
              <TextAsImage
                text={
                  isCanceled
                    ? `Canceled! ${concert.title}`
                    : isUnlisted
                      ? `${concert.title} (unlisted)`
                      : concert.title
                }
              />
            </button>
          )}
        </div>
      </div>

      <div className="mt-2">
        {concert.description && (
          <div
            className={styles.description}
            style={{ overflow: "hidden", maxHeight: "3em", lineHeight: "1.5" }}
          >
            <FreeTextFormat
              text={concert.description}
              expandable={false}
              expanded={false}
              linkify={true}
            />
          </div>
        )}

        <RelatedPosts
          postId={concert.id}
          nadaUrl={concert.nada_url}
          mode={config.showRelatedPosts}
        />

        <CommentSectionPreview
          postId={concert.id}
          postPath={concert.nada_url}
          mode={config.showComments}
        />

        <ReactionBlock
          contentType="Post"
          contentId={concert.id}
          postId={concert.id}
        />

        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}
      </div>
    </div>
  )
}
